import { createContext, useContext, useState, useEffect, createElement } from 'react';
import type { ReactNode } from 'react';

export type Theme = 'dark' | 'light';

export const STORAGE_KEY = 'cigs-theme';

export function resolveInitialTheme(stored: string | null, systemPrefersLight: boolean): Theme {
  if (stored === 'dark' || stored === 'light') return stored;
  return systemPrefersLight ? 'light' : 'dark';
}

/** A third state alongside the two applied themes: 'system' follows the OS preference live. */
export type ThemePreference = 'system' | Theme;

/** The applied theme for a given preference. 'system' resolves via the live OS signal. */
export function resolveTheme(preference: ThemePreference, systemPrefersLight: boolean): Theme {
  if (preference === 'system') return systemPrefersLight ? 'light' : 'dark';
  return preference;
}

/** dark -> light -> system -> dark — a single button cycles through all three states. */
export function nextThemePreference(current: ThemePreference): ThemePreference {
  if (current === 'dark') return 'light';
  if (current === 'light') return 'system';
  return 'dark';
}

function loadStoredPreference(): ThemePreference | null {
  if (typeof globalThis.localStorage === 'undefined') return null;
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'dark' || v === 'light' || v === 'system') return v;
    return null;
  } catch {
    return null;
  }
}

function readSystemPrefersLight(): boolean {
  return typeof globalThis.matchMedia !== 'undefined'
    ? matchMedia('(prefers-color-scheme: light)').matches
    : false;
}

interface ThemeContextValue {
  /** The applied theme (what's on the DOM right now). */
  theme: Theme;
  /** The user's stored preference — 'system' means no explicit choice has been made. */
  preference: ThemePreference;
  toggleTheme: () => void;
}

export const ThemeContext = createContext<ThemeContextValue>({
  theme: 'dark',
  preference: 'system',
  toggleTheme: () => {},
});

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreference] = useState<ThemePreference>(
    () => loadStoredPreference() ?? 'system',
  );
  const [systemPrefersLight, setSystemPrefersLight] = useState<boolean>(readSystemPrefersLight);

  // While the user has not made an explicit choice (preference === 'system'), an OS-level
  // theme change must be reflected without a reload.
  useEffect(() => {
    if (typeof globalThis.matchMedia === 'undefined') return;
    const mql = matchMedia('(prefers-color-scheme: light)');
    const handler = (e: MediaQueryListEvent) => setSystemPrefersLight(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  const theme = resolveTheme(preference, systemPrefersLight);

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [theme]);

  // Persist only an EXPLICIT choice. 'system' is not written to storage — that's what makes
  // it the default: a fresh visit (or a cleared preference) always re-derives from the OS.
  useEffect(() => {
    try {
      if (preference === 'system') {
        localStorage.removeItem(STORAGE_KEY);
      } else {
        localStorage.setItem(STORAGE_KEY, preference);
      }
    } catch {
      // ignore (e.g. privacy mode)
    }
  }, [preference]);

  const toggleTheme = () => setPreference((p) => nextThemePreference(p));

  return createElement(
    ThemeContext.Provider,
    { value: { theme, preference, toggleTheme } },
    children,
  );
}

export function useTheme(): ThemeContextValue {
  return useContext(ThemeContext);
}
